import IntakePrograms from "./pages/IntakePrograms";
import { DtpProgramIntake } from "./pages/dtp/DtpProgramIntake";
import { DtpProductPage } from './pages/dtp/DtpProductPage';
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Layout } from './components/layout/Layout';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Register } from './pages/Register';
import { RequestAccess } from './pages/RequestAccess';
import { Dashboard } from './pages/Dashboard';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import TelemedNetworks from './pages/TelemedNetworks';
import Requests from './pages/Requests';

import UserManagement from './pages/UserManagement';
import Users from './pages/Users';
import VolumeRewardsAdmin from './pages/VolumeRewardsAdmin';
import { PatientRoster } from './pages/PatientRoster';
import { PatientProfile } from './pages/PatientProfile';
import { PatientEdit } from './pages/PatientEdit';
import { Orders } from './pages/Orders';
import { PrescriptionTransfers } from './pages/PrescriptionTransfers';
import { CareInbox } from './pages/CareInbox';
import CatalogManagement from './pages/CatalogManagement';
import { PhysicianIDProofing } from './pages/PhysicianIDProofing';
import BulkOrders from './pages/BulkOrders';
import BulkOrdersList from './pages/BulkOrdersList';
import BulkOrderDetail from './pages/BulkOrderDetail';
import AdminInvoices from './pages/AdminInvoices';
import InvoiceTracking from './pages/InvoiceTracking';
import { AdminPayouts } from './pages/AdminPayouts';
import CommissionOverrides from './pages/CommissionOverrides';
import ClinicDeals from './pages/ClinicDeals';
import AdminShipments from './pages/AdminShipments';
import { AdminAR } from './pages/AdminAR';
import { AdminAP } from './pages/AdminAP';
import { RecipientPayouts } from './pages/RecipientPayouts';
import AdminOrders from './pages/AdminOrders';
import AdminFulfillment from './pages/AdminFulfillment';
import PharmacyConfig from './pages/PharmacyConfig';
import AdminShippingSettings from './pages/AdminShippingSettings';
import SupplyRules from './pages/SupplyRules';
import Analytics from './pages/Analytics';
import AdminAmRankings from './pages/AdminAmRankings';
import AdminPhysicianRankings from './pages/AdminPhysicianRankings';
import Settings from './pages/Settings';
import ClinicSiteSettings from './pages/ClinicSiteSettings';
import { ImpersonationDashboard } from './pages/ImpersonationDashboard';
import AdminBlasts from './pages/admin/AdminBlasts';
import AdminBanners from './pages/admin/AdminBanners';
import PeptideCalculator from './pages/PeptideCalculator';
import VideoTutorials from './pages/VideoTutorials';
import AdminUserActivity from './pages/AdminUserActivity';
import Ava from './pages/Ava';
import { AdminSupport } from './pages/AdminSupport';
import AdminPatients from './pages/AdminPatients';
import CatalogHierarchy from './pages/admin/CatalogHierarchy';
import { PatientPayment } from './pages/PatientPayment';
import { InvoicePayment } from './pages/InvoicePayment';
import { PartnerCardSetup } from './pages/PartnerCardSetup';
import { PartnerCheckout } from './pages/PartnerCheckout';
import { PartnerPhysicianOnboarding } from './pages/PartnerPhysicianOnboarding';
import { InvoicePaymentSuccess } from './pages/InvoicePaymentSuccess';
import { PaymentSuccess } from './pages/PaymentSuccess';
import { PaymentFailure } from './pages/PaymentFailure';
import { NotificationSettings } from './pages/NotificationSettings';
import { PendingApprovalDashboard } from './pages/PendingApprovalDashboard';
import { CompleteProfile } from './pages/CompleteProfile';
import PhysicianStore from './pages/PhysicianStore';
import PhysicianMembershipReviews from './pages/PhysicianMembershipReviews';
import { DtpShop } from './pages/dtp/DtpShop';
import { DtpCheckout } from './pages/dtp/DtpCheckout';
import { DtpOrderConfirmation } from './pages/dtp/DtpOrderConfirmation';
import { DtpOrders } from './pages/dtp/DtpOrders';
import { DtpRefills } from './pages/dtp/DtpRefills';
import { DtpLayout } from './components/dtp/DtpLayout';
import ApiTokens from './pages/ApiTokens';
import { DtpProtectedRoute } from './components/dtp/DtpProtectedRoute';
import { DtpLearn } from './pages/dtp/DtpLearn';
import { DtpLearnCategory } from './pages/dtp/DtpLearnCategory';
import { DtpLearnFamily } from './pages/dtp/DtpLearnFamily';
import DtpMembership from './pages/dtp/DtpMembership';
import DtpPortal from './pages/dtp/DtpPortal';
import { DtpHome } from './pages/dtp/DtpHome';
import { PatientLogin } from './pages/patient/PatientLogin';
import { PatientLoginLink } from './pages/patient/PatientLoginLink';
import { DtpAccount } from './pages/dtp/DtpAccount';
import { DtpResults } from './pages/dtp/DtpResults';
import { DtpBilling } from './pages/dtp/DtpBilling';
import { DtpReceipt } from './pages/dtp/DtpReceipt';
import { DtpResultDetail } from './pages/dtp/DtpResultDetail';
import { DtpMessages } from './pages/dtp/DtpMessages';
import { DtpSupport } from './pages/dtp/DtpSupport';
import { DtpAva } from './pages/dtp/DtpAva';
import { DtpTreatments } from './pages/dtp/DtpTreatments';
import { LabResultDetail } from './pages/LabResultDetail';
import { useAuthStore, useIsAuthenticated } from './store/useAuthStore';
import { useTenantBranding } from './hooks/useTenantBranding-runtime';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import { AnalyticsPageViews } from './components/analytics/AnalyticsPageViews';
import { isPatientFacingPath } from './lib/analytics';
import { PatientAnalytics, PatientInsights } from './pages/PatientAnalytics';

function App() {
  const { getCurrentUser } = useAuthStore();
  // Call useIsAuthenticated() to trigger re-renders on auth state change
  useIsAuthenticated();

  // Initialize tenant branding
  const { branding, isLoading } = useTenantBranding();

  // Auto logout after 1 hour of inactivity
  useInactivityTimeout();

  useEffect(() => {
    // IMPORTANT: Check for token in localStorage, not just cached isAuthenticated
    // This handles impersonation where we clear the cache but keep the token
    const token = localStorage.getItem('token');
    if (token) {
      getCurrentUser();
    }
  }, []); // Run once on mount

  // Show loading screen while tenant branding is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading {branding.companyName}...</p>
        </div>
      </div>
    );
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {/* Patient analytics: storefront, patient sign-in, portal and payment pages only. */}
      <AnalyticsPageViews include={isPatientFacingPath} />
      <div className="App">
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register" element={<Register />} />
          <Route path="/request-access" element={<RequestAccess />} />
          
          {/* Public payment routes - no authentication required */}
          <Route path="/payment/:paymentLinkId" element={<PatientPayment />} />
          <Route path="/payment/success/:paymentLinkId" element={<PaymentSuccess />} />
          <Route path="/payment/failure/:paymentLinkId" element={<PaymentFailure />} />

          {/* Invoice payment routes - for physicians paying invoices via email link */}
          <Route path="/invoice-payment/:token" element={<InvoicePayment />} />
          <Route path="/partner-card-setup" element={<PartnerCardSetup />} />
          <Route path="/partner-checkout" element={<PartnerCheckout />} />
          <Route path="/partner-onboarding" element={<PartnerPhysicianOnboarding />} />
          <Route path="/partner-onboarding/:id" element={<PartnerPhysicianOnboarding />} />
          <Route path="/invoice-payment/success/:token" element={<InvoicePaymentSuccess />} />

          {/* Top-level patient sign-in: any clinic, any method */}
          <Route path="/patient/login" element={<PatientLogin />} />
          <Route path="/patient/login/link" element={<PatientLoginLink />} />

          {/* Direct-to-Patient (DTP) storefront routes */}
          <Route path="/dtp/:physicianId" element={<DtpLayout />}>
            <Route index element={<DtpShop />} />
            <Route path="shop" element={<DtpShop />} />
            <Route path="products/:slug" element={<DtpProductPage />} />
            <Route path="intake/:programId" element={<DtpProgramIntake />} />
            <Route path="checkout" element={<DtpCheckout />} />
            <Route path="home" element={<DtpProtectedRoute><DtpHome /></DtpProtectedRoute>} />
            <Route path="account" element={<DtpProtectedRoute><DtpAccount /></DtpProtectedRoute>} />
            <Route path="orders" element={<DtpProtectedRoute><DtpOrders /></DtpProtectedRoute>} />
            <Route path="billing" element={<DtpProtectedRoute><DtpBilling /></DtpProtectedRoute>} />
            <Route path="billing/receipts/:receiptId" element={<DtpProtectedRoute><DtpReceipt /></DtpProtectedRoute>} />
            <Route path="messages" element={<DtpProtectedRoute><DtpMessages /></DtpProtectedRoute>} />
            <Route path="support" element={<DtpProtectedRoute><DtpSupport /></DtpProtectedRoute>} />
            <Route path="assistant" element={<DtpProtectedRoute><DtpAva /></DtpProtectedRoute>} />
            <Route path="treatments" element={<DtpProtectedRoute><DtpTreatments /></DtpProtectedRoute>} />
            <Route path="refills" element={<DtpProtectedRoute><DtpRefills /></DtpProtectedRoute>} />
            {/* Results gate themselves inline so a results link returns the
                patient to the report they tapped, not to the storefront. */}
            <Route path="results" element={<DtpResults />} />
            <Route path="results/:resultId" element={<DtpResultDetail />} />
            <Route path="orders/:orderId/confirmation" element={<DtpProtectedRoute><DtpOrderConfirmation /></DtpProtectedRoute>} />
          </Route>

          {/* DTP educational variant — outside DtpLayout to use its own branded shell */}
          <Route path="/dtp/:physicianId/learn" element={<DtpLearn />} />
          <Route path="/dtp/:physicianId/learn/:categorySlug" element={<DtpLearnCategory />} />
          <Route path="/dtp/:physicianId/learn/:categorySlug/:familySlug" element={<DtpLearnFamily />} />

          {/* DTP membership routes */}
          <Route path="/dtp/:physicianId/membership" element={<DtpMembership />} />
          <Route path="/dtp/:physicianId/portal" element={<DtpPortal />} />

          {/* Protected routes */}
          <Route path="/clinic-site" element={<ProtectedRoute allowedRoles={['physician']}><Layout><ClinicSiteSettings /></Layout></ProtectedRoute>} />
          <Route path="/admin/clinic-sites" element={<ProtectedRoute allowedRoles={['admin']}><Layout><ClinicSiteSettings admin /></Layout></ProtectedRoute>} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/products"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor', 'distributor', 'physician', 'physician_readonly']}>
                <Layout>
                  <Products />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route path="/intake-programs" element={<ProtectedRoute allowedRoles={['admin']}><Layout><IntakePrograms /></Layout></ProtectedRoute>} />
          <Route
            path="/products/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor', 'distributor', 'physician', 'physician_readonly']}>
                <Layout>
                  <ProductDetail />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/requests"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor', 'distributor', 'physician', 'physician_readonly']}>
                <Layout>
                  <Requests />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/telemed-networks"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <TelemedNetworks />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/catalogs"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor', 'distributor']}>
                <Layout>
                  <CatalogManagement />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Physician My Catalog removed; physicians use Products page */}

          <Route
            path="/patients"
            element={
              <ProtectedRoute allowedRoles={['physician', 'physician_readonly']}>
                <Layout>
                  <PatientRoster />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients/:id"
            element={
              <ProtectedRoute allowedRoles={['physician', 'physician_readonly']}>
                <Layout>
                  <PatientProfile />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/lab-results/:resultId"
            element={
              <ProtectedRoute allowedRoles={['physician', 'physician_readonly', 'admin']}>
                <Layout>
                  <LabResultDetail />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients/:id/edit"
            element={
              <ProtectedRoute allowedRoles={['physician']}>
                <Layout>
                  <PatientEdit />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders"
            element={
              <ProtectedRoute allowedRoles={['physician', 'physician_readonly']}>
                <Layout>
                  <Orders />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Patient portal messages and refill requests */}
          <Route
            path="/care-inbox"
            element={
              <ProtectedRoute allowedRoles={['physician', 'physician_readonly']}>
                <Layout>
                  <CareInbox />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Pharmacy accounts: outside doctors' scripts transferred in to fill */}
          <Route
            path="/prescription-transfers"
            element={
              <ProtectedRoute allowedRoles={['physician']}>
                <Layout>
                  <PrescriptionTransfers />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/store"
            element={
              <ProtectedRoute allowedRoles={['physician']}>
                <Layout>
                  <PhysicianStore />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/memberships/pending"
            element={
              <ProtectedRoute allowedRoles={['physician']}>
                <Layout>
                  <PhysicianMembershipReviews />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/id-proofing"
            element={
              <ProtectedRoute allowedRoles={['physician', 'physician_readonly']}>
                <PhysicianIDProofing />
              </ProtectedRoute>
            }
          />

          <Route
            path="/id-proofing/npi"
            element={
              <ProtectedRoute allowedRoles={['physician', 'physician_readonly']}>
                <PhysicianIDProofing />
              </ProtectedRoute>
            }
          />

          <Route
            path="/id-proofing/license"
            element={
              <ProtectedRoute allowedRoles={['physician', 'physician_readonly']}>
                <PhysicianIDProofing />
              </ProtectedRoute>
            }
          />

          <Route
            path="/id-proofing/selfie"
            element={
              <ProtectedRoute allowedRoles={['physician', 'physician_readonly']}>
                <PhysicianIDProofing />
              </ProtectedRoute>
            }
          />

          <Route
            path="/id-proofing/signature"
            element={
              <ProtectedRoute allowedRoles={['physician', 'physician_readonly']}>
                <PhysicianIDProofing />
              </ProtectedRoute>
            }
          />

          <Route
            path="/id-proofing/review"
            element={
              <ProtectedRoute allowedRoles={['physician', 'physician_readonly']}>
                <PhysicianIDProofing />
              </ProtectedRoute>
            }
          />

          {/* Pending review now renders the directory filtered to blockers that
              are ours to clear, rather than `status: 'pending'` alone. */}
          <Route
            path="/pending"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
                <Layout>
                  <Users />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* A partner's own integration credentials. The page itself is the
              gate: the backend refuses unless supervisorData.apiEnabled is on,
              so a supervisor who is not a partner sees the refusal, not a 404. */}
          <Route
            path="/api-tokens"
            element={
              <ProtectedRoute allowedRoles={['supervisor']}>
                <Layout>
                  <ApiTokens />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Single cross-role directory. Additive: the role-scoped routes
              below continue to serve the existing pages. */}
          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
                <Layout>
                  <Users />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/physicians"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor', 'distributor']}>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/supervisors"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admins"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/volume-rewards"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <VolumeRewardsAdmin />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/distributors"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/analytics"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor', 'distributor', 'physician']}>
                <Layout>
                  <Analytics />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/analytics/rankings/ams"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <AdminAmRankings />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/analytics/rankings/physicians"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <AdminPhysicianRankings />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor', 'distributor', 'physician']}>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/notification-settings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor', 'distributor', 'physician']}>
                <NotificationSettings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/bulk-orders"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor', 'distributor', 'physician']}>
                <Layout>
                  <BulkOrders />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/bulk-orders/list"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor', 'distributor', 'physician']}>
                <Layout>
                  <BulkOrdersList />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/bulk-orders/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'supervisor', 'distributor', 'physician']}>
                <Layout>
                  <BulkOrderDetail />
                </Layout>
              </ProtectedRoute>
            }
          />

              <Route
                path="/admin/orders"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminOrders />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/fulfillment"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminFulfillment />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/patients"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminPatients />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/support"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminSupport />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/invoices"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminInvoices />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoice-tracking"
                element={
                  <ProtectedRoute allowedRoles={['physician', 'supervisor', 'distributor']}>
                    <Layout>
                      <InvoiceTracking />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/impersonation"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <ImpersonationDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/payouts"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminPayouts />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/commission-overrides"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <CommissionOverrides />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/clinic-deals"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <ClinicDeals />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/shipments"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminShipments />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/ar"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminAR />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/ap"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminAP />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/pharmacy-config"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <PharmacyConfig />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/shipping-settings"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminShippingSettings />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/supply-rules"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <SupplyRules />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/blasts"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminBlasts />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/banners"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminBanners />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/learn/videos"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor', 'distributor', 'physician', 'physician_readonly']}>
                    <Layout>
                      <VideoTutorials />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/peptide-calculator"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <PeptideCalculator />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/ava"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'supervisor', 'distributor', 'physician', 'physician_readonly']}>
                    <Layout>
                      <Ava />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/admin/assistant" element={<Navigate to="/ava" replace />} />

              <Route
                path="/admin/patient-analytics"
                element={<ProtectedRoute allowedRoles={['admin']}><Layout><PatientAnalytics /></Layout></ProtectedRoute>}
              />
              <Route
                path="/patient-insights"
                element={<ProtectedRoute allowedRoles={['physician', 'physician_readonly']}><Layout><PatientInsights /></Layout></ProtectedRoute>}
              />

              <Route
                path="/admin/user-activity"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AdminUserActivity />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/catalog-hierarchy"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <CatalogHierarchy />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/payouts"
                element={
                  <ProtectedRoute allowedRoles={['supervisor', 'distributor', 'partner']}>
                    <Layout>
                      <RecipientPayouts />
                    </Layout>
                  </ProtectedRoute>
                }
              />

          {/* Pending Approval Dashboard - shown to users with pending status */}
          <Route
            path="/pending-approval"
            element={
              <ProtectedRoute>
                <PendingApprovalDashboard />
              </ProtectedRoute>
            }
          />

          {/* Complete Profile - shown to physicians missing required info */}
          <Route
            path="/complete-profile"
            element={
              <ProtectedRoute allowedRoles={['physician']}>
                <CompleteProfile />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
